/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import { AnimatePresence } from 'motion/react';
import { HelmetProvider } from 'react-helmet-async';
import ScrollToTop from './components/utils/ScrollToTop';
import FaviconHandler from './components/utils/FaviconHandler';
import Home from './pages/Home';
import { ThemeProvider } from './components/theme-provider';
import QuickActions from './components/ui/QuickActions';
import CookieConsentBanner from './components/ui/CookieConsentBanner';
import { LanguageProvider } from './contexts/LanguageContext';

const SolutionsGallery = lazy(() => import('./pages/SolutionsGallery'));
const SolutionsMarketScope = lazy(() => import('./pages/SolutionsMarketScope'));
const SolutionsCategory = lazy(() => import('./pages/SolutionsCategory'));
const SolutionPage = lazy(() => import('./pages/SolutionPage'));
const MarketIndexPage = lazy(() => import('./pages/MarketIndexPage'));
const MarketIndividualPage = lazy(() => import('./pages/MarketIndividualPage'));
const Partners = lazy(() => import('./pages/Partners'));
const PressRoom = lazy(() => import('./pages/PressRoom'));
const PressArchive = lazy(() => import('./pages/PressArchive'));
const PressReleasePage = lazy(() => import('./pages/PressReleasePage'));
const Contact = lazy(() => import('./pages/Contact'));
const About = lazy(() => import('./pages/About'));
const SolutionsInventory = lazy(() => import('./pages/SolutionsInventory'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfUse = lazy(() => import('./pages/TermsOfUse'));
const NotFound = lazy(() => import('./pages/NotFound'));

const TechnologyPartners = lazy(() => import('./pages/partners/TechnologyPartners'));
const CommercialPartners = lazy(() => import('./pages/partners/CommercialPartners'));
const BusinessPartners = lazy(() => import('./pages/partners/BusinessPartners'));
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));

function AppShell() {
  const location = useLocation();
  const isAdminArea = location.pathname.startsWith('/admin');

  return (
    <div className="flex flex-col min-h-screen">
      {!isAdminArea && <Header />}
      <main className={isAdminArea ? '' : 'flex-grow'}>
        <AnimatePresence mode="wait">
          <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/sobre-nos" element={<About />} />
            <Route path="/solucoes" element={<SolutionsGallery />} />
            <Route path="/solucoes/:marketId/:scope" element={<SolutionsMarketScope />} />
            <Route path="/solucoes/:marketId/:scope/:categoryId" element={<SolutionsCategory />} />
            <Route path="/solucoes/inventario" element={<SolutionsInventory />} />
            <Route path="/solucoes/:solutionId" element={<SolutionPage />} />
            <Route path="/servicos/:solutionId" element={<SolutionPage />} />
            <Route path="/mercados" element={<MarketIndexPage />} />
            <Route path="/mercados/:marketId" element={<MarketIndividualPage />} />
            <Route path="/sala-de-imprensa" element={<PressRoom />} />
            <Route path="/sala-de-imprensa/press-releases" element={<PressArchive />} />
            <Route path="/sala-de-imprensa/press-releases/:id" element={<PressReleasePage />} />
            <Route path="/parceiros" element={<Partners />} />
            <Route path="/parceiros/tecnologia" element={<TechnologyPartners />} />
            <Route path="/parceiros/comerciais" element={<CommercialPartners />} />
            <Route path="/parceiros/clientes-e-negocios" element={<BusinessPartners />} />
            <Route path="/contato" element={<Contact />} />
            <Route path="/privacidade" element={<PrivacyPolicy />} />
            <Route path="/termos-de-uso" element={<TermsOfUse />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </AnimatePresence>
      </main>
      {!isAdminArea && <Footer />}
      {!isAdminArea && <QuickActions />}
      <CookieConsentBanner />
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider defaultTheme="light" storageKey="techenabler-theme">
      <LanguageProvider>
        <FaviconHandler />
        <HelmetProvider>
          <Router>
            <ScrollToTop />
            <AppShell />
          </Router>
        </HelmetProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}




